import './styles.scss';

import React from 'react';
import { Slider as MuiSlider } from '@mui/material';
import { Text } from '@fragments';

/**
 * Slider — a value slider following the "recessed well + raised chip" metaphor.
 *
 * Wraps MUI's <Slider> so dragging, keyboard control and a11y come for free,
 * then restyles every slot via `sx` with our design tokens:
 *   - rail   → recessed well   (var(--surface-8))
 *   - track  → single accent    (var(--primary-6))
 *   - thumb  → raised card chip  (var(--surface-10) + hairline + soft shadow)
 *
 * Supports a single value or a [a, b] range (two thumbs) via `range`.
 */
export default function Slider({
    value,
    onChange,
    min = 0,
    max = 100,
    step = 1,
    disabled = false,
    marks = false,
    valueLabel = 'auto',
    formatLabel,
    range = false,
    label,
    className = '',
    ...props
}) {
    // Map our friendly `valueLabel` prop onto MUI's valueLabelDisplay.
    const valueLabelDisplay =
        valueLabel === 'on' ? 'on' : valueLabel === 'off' ? 'off' : 'auto';

    // Resolve the current value(s) for the optional inline read-out on the right.
    const formatOne = (v) =>
        typeof formatLabel === 'function' ? formatLabel(v) : `${v}`;

    const readout = range
        ? Array.isArray(value) && value.length === 2
            ? `${formatOne(value[0])} – ${formatOne(value[1])}`
            : null
        : value != null
            ? formatOne(value)
            : null;

    const sx = {
        height: 6,
        padding: '13px 0', // generous vertical hit area, visually thin rail
        color: 'var(--primary-6)',

        // Recessed well — the rail behind the filled portion.
        '& .MuiSlider-rail': {
            backgroundColor: 'var(--surface-8)',
            opacity: 1,
            height: 6,
            borderRadius: 'var(--radius-pill)',
        },

        // Filled range — the single accent.
        '& .MuiSlider-track': {
            backgroundColor: 'var(--primary-6)',
            border: 'none',
            height: 6,
            borderRadius: 'var(--radius-pill)',
        },

        // Raised knob — reads as a card chip sitting above the well.
        '& .MuiSlider-thumb': {
            width: 18,
            height: 18,
            backgroundColor: 'var(--surface-10)',
            border: '1px solid var(--surface-7)',
            borderRadius: 'var(--radius-pill)',
            boxShadow: 'var(--shadow-xs)',
            transition:
                'box-shadow 0.2s ease, border-color 0.2s ease, transform 0.2s ease',

            '&:hover, &.Mui-focusVisible': {
                borderColor: 'var(--surface-6)',
                boxShadow: '0 0 0 6px var(--primary-bg)',
            },
            // Active (grabbing) — a touch larger ring, no jumpiness.
            '&.Mui-active': {
                boxShadow: '0 0 0 8px var(--primary-bg)',
            },
            // Kill MUI's default focus shadow on the inner before pseudo.
            '&::before': {
                boxShadow: 'none',
            },
        },

        // Value bubble — small dark pill, surface-2 on surface-10 text.
        '& .MuiSlider-valueLabel': {
            backgroundColor: 'var(--surface-2)',
            color: 'var(--surface-10)',
            borderRadius: 'var(--radius-sm)',
            padding: '2px 8px',
            fontSize: '12px',
            lineHeight: 1.4,
            fontWeight: 500,
            top: -6,
            '&::before': {
                color: 'var(--surface-2)',
            },
            '& *': {
                background: 'transparent',
                color: 'inherit',
            },
        },

        // Marks — muted ticks on the well.
        '& .MuiSlider-mark': {
            backgroundColor: 'var(--surface-6)',
            width: 3,
            height: 3,
            borderRadius: 'var(--radius-pill)',
            opacity: 1,
            '&.MuiSlider-markActive': {
                backgroundColor: 'var(--primary-6)',
            },
        },

        '& .MuiSlider-markLabel': {
            color: 'var(--surface-6)',
            fontSize: '12px',
            top: 26,
        },

        // Disabled — muted, no pointer, dim thumb.
        '&.Mui-disabled': {
            color: 'var(--surface-7)',
            opacity: 0.55,
            pointerEvents: 'none',
            '& .MuiSlider-track': {
                backgroundColor: 'var(--surface-7)',
            },
            '& .MuiSlider-thumb': {
                backgroundColor: 'var(--surface-9)',
                borderColor: 'var(--surface-7)',
                boxShadow: 'none',
            },
        },
    };

    return (
        <div className={`gs-slider ${disabled ? 'gs-slider--disabled' : ''} ${className}`.trim()}>
            {(label || readout) && (
                <div className="gs-slider__head">
                    {label && <Text.label>{label}</Text.label>}
                    {readout && (
                        <Text.span contrast={6} className="gs-slider__readout">
                            {readout}
                        </Text.span>
                    )}
                </div>
            )}

            <MuiSlider
                value={value}
                onChange={onChange}
                min={min}
                max={max}
                step={step}
                disabled={disabled}
                marks={marks}
                valueLabelDisplay={valueLabelDisplay}
                valueLabelFormat={formatLabel}
                disableSwap
                sx={sx}
                {...props}
            />
        </div>
    );
}
